import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';
import { Card, CardContent } from './ui/card';
import { Store, Eye, MousePointerClick, CalendarCheck, DollarSign, Globe2, Tag, Megaphone, RefreshCw, UsersRound, Download } from 'lucide-react';

const PERIODS = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
  { days: 365, label: '1y' },
];

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-gray-500 text-xs font-medium uppercase tracking-wide">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-semibold text-gray-900 mt-1.5">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const BreakdownTable = ({ title, icon: Icon, rows, keyLabel, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
        <Icon className="w-4 h-4 text-teal-600" /> {title}
      </div>
      {(!rows || rows.length === 0) ? (
        <p className="text-sm text-gray-400 py-4 text-center">No data yet for this period</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-400 border-b border-gray-100">
              <th className="text-left font-medium pb-2">{keyLabel}</th>
              <th className="text-right font-medium pb-2">Visits</th>
              <th className="text-right font-medium pb-2">Bookings</th>
              <th className="text-right font-medium pb-2">GMV</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-gray-50 last:border-0">
                <td className="py-2 text-gray-700 font-medium">{r.key}</td>
                <td className="py-2 text-right text-gray-500">{r.visits || 0}</td>
                <td className="py-2 text-right text-gray-700">{r.bookings || 0}</td>
                <td className="py-2 text-right text-gray-900 font-medium">{fmtMoney(r.gmv)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardContent>
  </Card>
);

export const SuperAdminMarketplaceTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('jwtToken');
      const res = await axios.get(`${BACKEND_URL}/api/v2/super-admin/marketplace/analytics?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load marketplace analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const funnel = data?.funnel || {};
  const pct = (num, den) => (den > 0 ? `${((num / den) * 100).toFixed(1)}%` : '—');

  const [customers, setCustomers] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    axios.get(`${BACKEND_URL}/api/v2/super-admin/marketplace/customers?days=365`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => setCustomers(res.data)).catch(() => setCustomers({ count: 0, customers: [] }));
  }, []);

  const exportCustomersCsv = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const res = await axios.get(`${BACKEND_URL}/api/v2/super-admin/marketplace/customers?days=365&format=csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `marketplace_customers_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* toast-free, button stays */ }
  };

  return (
    <div className="space-y-5" data-testid="marketplace-analytics-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Store className="w-5 h-5 text-teal-600" /> Marketplace Intelligence
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Cross-org funnel for BookingFlows Marketplace traffic and BM- bookings
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              data-testid={`marketplace-period-${p.label}`}
              onClick={() => setDays(p.days)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                days === p.days ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={load}
            data-testid="marketplace-refresh-btn"
            className="ml-1 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="marketplace-analytics-error">
          {error}
        </div>
      )}

      {/* Funnel with stage-to-stage conversion (drop-off) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Eye} label="Visits" value={funnel.visits ?? '—'} sub="unique sessions" testId="mkt-stat-visits" />
        <StatCard icon={MousePointerClick} label="Experience Views" value={funnel.experienceViews ?? '—'} sub={`${pct(funnel.experienceViews || 0, funnel.visits || 0)} of visits`} testId="mkt-stat-views" />
        <StatCard icon={MousePointerClick} label="Checkout Starts" value={funnel.checkoutStarts ?? '—'} sub={`${pct(funnel.checkoutStarts || 0, funnel.experienceViews || 0)} of views`} testId="mkt-stat-checkouts" />
        <StatCard icon={CalendarCheck} label="Bookings" value={funnel.bookings ?? '—'} sub={`${funnel.paidBookings || 0} paid · ${pct(funnel.bookings || 0, funnel.checkoutStarts || 0)} of checkouts`} testId="mkt-stat-bookings" />
        <StatCard icon={DollarSign} label="GMV" value={fmtMoney(funnel.gmv)} testId="mkt-stat-gmv" />
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownTable title="By Country" icon={Globe2} rows={data?.byCountry} keyLabel="Country" testId="mkt-by-country" />
        <BreakdownTable title="By Category" icon={Tag} rows={data?.byCategory} keyLabel="Category" testId="mkt-by-category" />
        <BreakdownTable title="By Campaign (UTM)" icon={Megaphone} rows={data?.byUtm} keyLabel="Source / Campaign" testId="mkt-by-utm" />
      </div>

      {/* Marketplace customers — future ad audiences */}
      <Card data-testid="mkt-customers">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <UsersRound className="w-4 h-4 text-teal-600" />
              Marketplace Customers
              <span className="text-xs font-normal text-gray-400">({customers?.count ?? '…'} · last 12 months)</span>
            </div>
            <button
              onClick={exportCustomersCsv}
              data-testid="mkt-export-customers-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
          {(!customers || customers.customers?.length === 0) ? (
            <p className="text-sm text-gray-400 py-6 text-center">
              Customers who book through the marketplace will build up here — your future ad audience.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-400 border-b border-gray-100">
                    <th className="text-left font-medium pb-2">Customer</th>
                    <th className="text-left font-medium pb-2">Phone</th>
                    <th className="text-left font-medium pb-2">Country</th>
                    <th className="text-left font-medium pb-2">Last Experience</th>
                    <th className="text-left font-medium pb-2">First Source</th>
                    <th className="text-right font-medium pb-2">Bookings</th>
                    <th className="text-right font-medium pb-2">GMV</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.customers.slice(0, 15).map((c) => (
                    <tr key={c.email} className="border-b border-gray-50 last:border-0">
                      <td className="py-2">
                        <div className="text-gray-800 font-medium">{c.name || '—'}</div>
                        <div className="text-xs text-gray-400">{c.email}</div>
                      </td>
                      <td className="py-2 text-gray-500 text-xs">{c.phone || '—'}</td>
                      <td className="py-2 text-gray-500">{c.country}</td>
                      <td className="py-2 text-gray-500 text-xs">{c.lastExperience || '—'}</td>
                      <td className="py-2 text-gray-500 text-xs">{c.firstUtmSource || 'organic'}</td>
                      <td className="py-2 text-right text-gray-700">{c.bookings}</td>
                      <td className="py-2 text-right text-gray-900 font-medium">
                        {c.currency === 'AED' ? 'AED ' : '$'}{Number(c.gmv).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent bookings */}
      <Card data-testid="mkt-recent-bookings">
        <CardContent className="p-4">
          <div className="text-sm font-semibold text-gray-900 mb-3">Recent Marketplace Bookings</div>
          {(!data?.recentBookings || data.recentBookings.length === 0) ? (
            <p className="text-sm text-gray-400 py-6 text-center">
              No marketplace bookings in this period yet — they'll appear here with their BM- codes once ads start converting.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-400 border-b border-gray-100">
                    <th className="text-left font-medium pb-2">Code</th>
                    <th className="text-left font-medium pb-2">Experience</th>
                    <th className="text-left font-medium pb-2">Operator</th>
                    <th className="text-left font-medium pb-2">Country</th>
                    <th className="text-left font-medium pb-2">Campaign</th>
                    <th className="text-right font-medium pb-2">Amount</th>
                    <th className="text-right font-medium pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentBookings.map((b) => (
                    <tr key={b.confirmationCode} className="border-b border-gray-50 last:border-0">
                      <td className="py-2 font-mono text-xs text-teal-700">{b.confirmationCode}</td>
                      <td className="py-2 text-gray-700">{b.serviceName || '—'}</td>
                      <td className="py-2 text-gray-500">{b.operatorName}</td>
                      <td className="py-2 text-gray-500">{b.country}</td>
                      <td className="py-2 text-gray-500 text-xs">
                        {b.utmSource ? `${b.utmSource}${b.utmCampaign ? ` / ${b.utmCampaign}` : ''}` : 'organic'}
                      </td>
                      <td className="py-2 text-right text-gray-900 font-medium">
                        {b.currency === 'AED' ? 'AED ' : '$'}{Number(b.amount).toLocaleString()}
                      </td>
                      <td className="py-2 text-right">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          b.paymentStatus === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {b.paymentStatus === 'paid' ? 'Paid' : b.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminMarketplaceTab;
